<template>    
  <ion-page id="lichessplay-page">
    <AlertReplacement :alertInfo="alertInfo" :trigger="alertTrigger"
              @alert-result="alertResult"></AlertReplacement>
    <ion-content>
  
      <div :style="styleBoard">
        <CMChessboard :fen="forceFen" :validMoves="availableMoves" 
                :orientation="orientation" 
                :boardSize="boardSize" 
                :reportFromTo="showEyegazeButtons" 
                :animateOnePly="animateFen"
                @board-move="boardMove"  />
      </div>
      <div :class="{ 'text-dark' : isDark,'text-light' : !isDark }" 
                        :style="styleTextArea">
        <div v-html="textareaMessage"></div>
      </div>

      <div v-if="showEyegazeButtons" :class="{ 'text-dark' : isDark,'text-light' : !isDark }" 
                        :style="styleEyeGaze">
        <div  id="eyegaze_area" style="display: inline;">
          <div v-for="row in eyegazeMatrix" >
              <span v-for="col in row.items" >
                <span v-if="col != null">
                  <span >
                    <ion-button @click="eyegazeClick(col)" 
                          :class="{ 'one-move-button' : col.moves.length == 1,
                                    'multiple-move-button' : col.moves.length > 1,
                                    'single-height' : eyegazeMatrix.length == 4,
                                    'three-fourth-height' : eyegazeMatrix.length == 3,
                                    'double-height' : eyegazeMatrix.length <= 2 }">
                        <ion-img class="chess-image" style="text-align: left;" :src="col.img" alt="Chessvis"></ion-img>
                      <span class="button-text">   {{ col.info }}</span>
                    </ion-button>
                  </span>
                </span>
              </span>
          </div>
        </div>
      </div>

      <div :class="{ 'text-dark' : isDark,'text-light' : !isDark }" 
                        :style="styleOpponent">
        <div class="name-text">{{  opponentNameDisplay }}</div>
        <div class="time-text">{{  opponentTimeDisplay }}</div>      
      </div>
      <div :class="{ 'text-dark' : isDark,'text-light' : !isDark }" 
                        :style="stylePlayer">
        <div class="name-text">{{ statusDisplay }}</div>
        <div class="time-text">{{ playerTimeDisplay }}</div>
      </div>
    </ion-content>
    <Toolbar :elements="toolbarElements" :trigger="toolbarTrigger" @toolbar-click="toolbarClick" />

  </ion-page>
</template>

<script setup>

  import { IonButton,IonImg,IonAlert,IonIcon,IonPage, IonContent,modalController  } from '@ionic/vue';

  import AlertReplacement from '@/components/AlertReplacement.vue'
  import ModalErrorMessage from '@/components/ModalErrorMessage.vue'

  import CMChessboard from '@/components/CMChessboard.vue'

import { ref, onMounted, onBeforeUnmount, watch } from 'vue'

import Toolbar from '@/components/Toolbar.vue'
import party from "party-js";
import { useConnectionStore } from '@/stores/connectionStore';
import { doStartupCheck } from '@/composables/startupCheck.js'
import { availableWidthHeight, calculateChessBoardRect ,isScreenPortraitOrientation } from '@/composables/screenSize'
import { useWindowSizeStore } from '@/stores/windowSizeStore';
import { useGetData , generateErrorAlertText, getLichessData, Utf8ArrayToStr } from '@/composables/getData.js'
import { t , replaceMultipleAll } from '@/composables/translateText.js'
import ModalAlertReplacement from '@/components/ModalAlertReplacement.vue';
import { buildPieceLocationStrings, getPieceSetName, columnToNumber, squarePairToAlgebraic,
    buildAvailableMoves, buildEyegazeButtons } from '@/composables/blindfoldSupport.js'

import { Chess } from '@/ts/chess.ts'

import { useStringStore } from '@/stores/stringStore';

const emit = defineEmits(['next-item-click'])

const TEXTAREA_FONTSIZE = 16

const NO_DRAW_ON_TABLE = 1
const PLAYER_OFFERED_DRAW = 2
const OPPONENT_OFFERED_DRAW = 3

const NO_GAME = 0
const IN_GAME = 1
const GAME_ENDED = 2

var gameState = NO_GAME

const INITIAL_FEN = "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1"

const stringStore = useStringStore()

const connectionStore = useConnectionStore()
var playType = connectionStore.getPuzzleType
const isSubscriber = ref(connectionStore.getIsSubscriber == 1)

const lichessAccessToken = connectionStore.getLichessAccessToken
var lichessURLGameStream = ""
const lichessUserid = connectionStore.getLichessUserid

var isDoneWithLichessStream = false
var hasActiveLichessStream = false

const optionDisplayArray = ref([])
const optionDisplaySelected = ref("")
const optionHeading = ref("")

const lichessNoTimerValue = 2147483647
var opponentSecondsLeft = 0
var playerSecondsLeft = 0
var playingColorString = "?"
var opponentUsername = ""
var gameTimerIsCountingDown = false
var gameTimerAgainstPlayer = false
var timerAmount = 0
var timerCanStop = false
var stopWaiting = false
var drawStatus = NO_DRAW_ON_TABLE
var gameIsOver = 0
var playerInCheck = 0
var processingGameFull = false

var displayFen = INITIAL_FEN

const textareaMessage = ref("<p><b>" + t("Loading Game") + "</b>")

var openGameId = connectionStore.getLichessGameId
var moveNumber = 0

const isDark = ref(connectionStore.getDarkmode != "light")
const menuTrigger = ref(0)

const whichMenu = localStorage.getItem("whichMenu")
const showBigButtons = ref(whichMenu == "buttons" || connectionStore.getShowBigButtons)
const showEyegazeButtons = ref(connectionStore.getIsEyeGaze)
var canUndoBigButtonSelection = false
const eyegazeMatrix = ref([[]])
var blindnessLevel = 1
var isPortrait = true

const alertInfo = ref({})
const alertTrigger = ref(0)

var hiddenAvailableMoves = []
var chess = new Chess()

const opponentNameDisplay = ref("Opponent Name")
const opponentTimeDisplay = ref("Opponent Time")
const playerTimeDisplay = ref("Player Time")
const statusDisplay = ref("status")

const TOOLBAR_RESIGN = 0
const TOOLBAR_OPEN_GAME_LIST = 1
const TOOLBAR_UNDO = 2

const toolbarElements = ref([
  { index : TOOLBAR_RESIGN, label : t("Resign") ,disabled : false},
  { index : TOOLBAR_OPEN_GAME_LIST, label : t("Goto Open List") ,disabled : false}
])

if (showEyegazeButtons.value)
  toolbarElements.value.push({ index : TOOLBAR_UNDO, label : t("Undo") ,disabled : true})

const toolbarTrigger = ref(0)

const windowSizeStore = useWindowSizeStore()
const windowSizeRef = ref({ width : windowSizeStore.getWindowSize['width'],
                      height : windowSizeStore.getWindowSize['height'] })
const boardSize = ref(100)
var blankAppendTrigger = ""

const initialFen = ref(INITIAL_FEN)
const forceFen = ref(INITIAL_FEN)
const animateFen = ref([])
const pieceSet = ref("regular")

var playingWhite = 1
var orientation = ref("white")

const availableMoves = ref([])
var availableMovesForTakeback = []
var playerSans = []

// callback for stream processing
var streamController = null
var streamAbortCallback = null

// string sent to lichess to report if there is an error
var urlSentToLichess = ""

const styleBoard = ref({
  position : "absolute",
  top : "0px",
  left : "100px",
  width: "100px",
  height : "100px"
})

const styleTextArea = ref({
  position : "absolute",
  "overflow-y" : "scroll",
  top : "0px",
  left : "100px",
  width: "100px",
  height : "100px",
  "font-size" : TEXTAREA_FONTSIZE + "px",
  "text-align" : "center",
  "line-height" : "1.2em"
})

const styleEyeGaze = ref({
  position : "absolute",
  top : "0px",
  left : "100px",
  width: "100px",
  height : "100px"
})

const styleOpponent = ref({
  position : "absolute",
  "overflow-y" : "scroll",
  top : "0px",
  left : "100px",
  width: "100px",
  height : "100px"
})

const stylePlayer = ref({
  position : "absolute",
  "overflow-y" : "scroll",
  top : "0px",
  left : "100px",
  width: "100px",
  height : "100px"
})

var controller = null

doStartupCheck(false,afterStartupCallbackRoutine)

const openErrorModal = async (programName,errorMessage) => {
    const modal = await modalController.create({
      component: ModalErrorMessage,
      componentProps: {
        type : "commError",
        program : programName ,
        errorMessage : errorMessage
      }
    });

    modal.present();

    const { data, role } = await modal.onWillDismiss();
  };

function afterStartupCallbackRoutine(stuffChanged,errorProgram,errorEntry) {
  if (errorProgram != null)
      openErrorModal(errorProgram,errorEntry)

  lichessURLGameStream = "https://lichess.org/api/board/game/stream/" + openGameId
  setupFetch(lichessURLGameStream,lichessAccessToken)

  updateWidthHeights()
  menuTrigger.value++
}

  onMounted(() => {
        updateWidthHeights()
  })

function updateStyleObjects(boundaries) {
  boardSize.value = boundaries['board'].height

  styleBoard.value.height = boundaries['board'].height + "px"
  styleBoard.value.width = boundaries['board'].width + "px"
  styleBoard.value.top = boundaries['board'].top + "px"
  styleBoard.value.left = boundaries['board'].left + "px"

  styleTextArea.value.height = boundaries['textArea'].height + "px"
  styleTextArea.value.width = boundaries['textArea'].width + "px"
  styleTextArea.value.top = boundaries['textArea'].top + "px"
  styleTextArea.value.left = boundaries['textArea'].left + "px"

  styleEyeGaze.value.height = boundaries['eyeGaze'].height + "px"
  styleEyeGaze.value.width = boundaries['eyeGaze'].width + "px"
  styleEyeGaze.value.top = boundaries['eyeGaze'].top + "px"
  styleEyeGaze.value.left = boundaries['eyeGaze'].left + "px"

  styleOpponent.value.height = boundaries['opponent'].height + "px"
  styleOpponent.value.width = boundaries['opponent'].width + "px"
  styleOpponent.value.top = boundaries['opponent'].top + "px"
  styleOpponent.value.left = boundaries['opponent'].left + "px"

  stylePlayer.value.height = boundaries['player'].height + "px"
  stylePlayer.value.width = boundaries['player'].width + "px"
  stylePlayer.value.top = boundaries['player'].top + "px"
  stylePlayer.value.left = boundaries['player'].left + "px"

  const buttonHeight = boundaries['eyeGaze'].height * .85 / 4
  var element = document.getElementById("eyegaze_area")
  if (element != null) {
    element.style.setProperty("--move_button_size", buttonHeight + "px");
    element.style.setProperty("--move_button_size_x1_33", (1.33 * buttonHeight) + "px");
    element.style.setProperty("--move_button_size_x2", (2 * buttonHeight) + "px");
  }

  //console.log("**** boardsize ",boardSize.value)
}

function updateWidthHeights() {

  const availableSpace = availableWidthHeight()
  isPortrait = isScreenPortraitOrientation(availableSpace)
  const boundaries = (isPortrait) ? calculatePortraitWidthHeights(availableSpace) :
                                      calculateLandscapeWidthHeights(availableSpace)

  updateStyleObjects(boundaries)
}

function calculatePortraitWidthHeights(availableSpace) {

  // reserve space for time and moves
  const availableSpaceForBoard = { width : availableSpace.width ,
              height : availableSpace.height - TEXTAREA_FONTSIZE * 6 * 1.4,
              isPortrait : true }

  const boardDimensions = calculateChessBoardRect(availableSpaceForBoard,1.0)

  const opponentPositionTop = 0
  const opponentPositionLeft = availableSpace.leftRightIndent
  const opponentHeight = TEXTAREA_FONTSIZE + 6
  const opponentWidth = availableSpace.width - availableSpace.leftRightIndent * 2

  const boardHeight = boardDimensions.height
  const boardWidth = boardDimensions.width
  const boardPositionTop = opponentPositionTop + opponentHeight + boardDimensions.y
  const boardPositionLeft = boardDimensions.x + availableSpace.leftRightIndent

  const playerPositionTop = boardPositionTop + boardHeight
  const playerPositionLeft = availableSpace.leftRightIndent
  const playerHeight = TEXTAREA_FONTSIZE + 6
  const playerWidth = availableSpace.width - availableSpace.leftRightIndent * 2

  const textAreaPositionTop = playerPositionTop + playerHeight
  const textAreaPositionLeft = availableSpace.leftRightIndent
  const textAreaHeight = availableSpace.height - textAreaPositionTop
  const textAreaWidth = availableSpace.width - availableSpace.leftRightIndent * 2

  const result = { board : {
      width : boardWidth,
      height : boardHeight,
      left : boardPositionLeft + availableSpace.leftIndent,
      top : boardPositionTop+ availableSpace.topIndent
    },
    textArea : {
      width : textAreaWidth,
      height : textAreaHeight,
      left : textAreaPositionLeft + availableSpace.leftIndent,
      top : textAreaPositionTop+ availableSpace.topIndent
    },
    opponent : {
      width : opponentWidth,
      height : opponentHeight,
      left : opponentPositionLeft + availableSpace.leftIndent,
      top : opponentPositionTop+ availableSpace.topIndent
    },
    player : {
      width : playerWidth,
      height : playerHeight,
      left : playerPositionLeft + availableSpace.leftIndent,
      top : playerPositionTop+ availableSpace.topIndent
    }
  }
  //console.log("board and movelist dimensions",result)
  return (result)
}

function calculateLandscapeWidthHeights(availableSpace) {

  const spaceReservedForBoard = (showEyegazeButtons.value) ? availableSpace.width / 2 : availableSpace.width - 150

  const availableSpaceForBoard = { width : spaceReservedForBoard,
                height : availableSpace.height ,
                isPortrait : false }
  const boardDimensions = calculateChessBoardRect(availableSpaceForBoard,1.0)
  
  const boardHeight = boardDimensions.height
  const boardWidth = boardDimensions.width
  const boardPositionTop = boardDimensions.y
  const boardPositionLeft = boardDimensions.x + availableSpace.leftRightIndent

  const opponentPositionTop = 0
  const opponentPositionLeft = boardPositionLeft + boardWidth + availableSpace.leftRightIndent
  const opponentHeight = TEXTAREA_FONTSIZE + 6
  const opponentWidth = availableSpace.width - opponentPositionLeft

  const playerHeight = opponentHeight
  const playerPositionTop = availableSpace.height - playerHeight
  const playerPositionLeft = opponentPositionLeft
  const playerWidth = opponentWidth

  const textAreaPositionTop = opponentPositionTop + opponentHeight
  const textAreaPositionLeft = opponentPositionLeft
  var textAreaHeight = (showEyegazeButtons.value) ? TEXTAREA_FONTSIZE * 3 : availableSpace.height - opponentHeight - playerHeight
  const textAreaWidth = opponentWidth
  
  const eyeGazePositionTop = textAreaPositionTop + textAreaHeight
  const eyeGazePositionLeft = opponentPositionLeft
  var eyeGazeHeight = availableSpace.height - textAreaHeight - opponentHeight - playerHeight
  const eyeGazeWidth = opponentWidth

  const result = { board : {
      width : boardWidth,
      height : boardHeight,
      left : boardPositionLeft + availableSpace.leftIndent,
      top : boardPositionTop+ availableSpace.topIndent
    },
    textArea : {
      width : textAreaWidth,
      height : textAreaHeight,
      left : textAreaPositionLeft + availableSpace.leftIndent,
      top : textAreaPositionTop+ availableSpace.topIndent
    },
    eyeGaze : {
      width : eyeGazeWidth,
      height : eyeGazeHeight,
      left : eyeGazePositionLeft + availableSpace.leftIndent,
      top : eyeGazePositionTop+ availableSpace.topIndent
    },
    opponent : {
      width : opponentWidth,
      height : opponentHeight,
      left : opponentPositionLeft + availableSpace.leftIndent,
      top : opponentPositionTop+ availableSpace.topIndent
    },
    player : {
      width : playerWidth,
      height : playerHeight,
      left : playerPositionLeft + availableSpace.leftIndent,
      top : playerPositionTop+ availableSpace.topIndent
    }
  }
  //console.log("board and movelist dimensions",result)
  return (result)
}

watch(() => windowSizeStore.getWindowSize, () => {
    //console.log('*********** Explorer -- window size changes detected')
    windowSizeRef.value = { width : windowSizeStore.getWindowSize['width'],
                      height : windowSizeStore.getWindowSize['height'] }
    //console.log("*** Explorer new window size",windowSizeRef.value)
    updateWidthHeights()
    menuTrigger.value++
  })

function saveControllerCallback(abortFunction,controllerName) {
  //console.log("**** In Save Controller",abortFunction,controllerName)
  streamController = controllerName
  streamAbortCallback = abortFunction

}

 // START ****************** MOVED FROM AsyncLichessStream

  function abortFunction(controller) {
    //console.log("******* IN Abort Function",controller)
    if (controller != null) {
      try {
        controller.abort()
      }
      catch (error) {
        console.log("abort error",error)
      }
    }
    controller = null
    //console.log("end abort function")
  }

  async function setupFetch (url,accessToken) {
    if (url == "" || accessToken == "")
      return

    //console.log("SETUP FETCH",url,accessToken)

    controller = new AbortController()

    var response = null
    try {
      response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept' : 'application/json',
          'Authorization' : "Bearer " + accessToken
        },
        signal : controller.signal
      })
    }
    catch(error) {
      console.error('Error:', error);
    }

    const reader = (response == null) ? null : response.body.getReader();

    // check if we're done with the stream on each loop. Calling program sets 'isDone'
    while (reader != null && controller != null && isDoneWithLichessStream == false) {
      const {value, done} = await reader.read();
      if (controller == null || done) 
        break;
      const receivedString = Utf8ArrayToStr(value)
      receivedEventString(receivedString)
      //console.log('Received', receivedString,value,isDoneWithLichessStream);
    }

    receivedEventString("ABORT")
    //console.log("before abort call")
    abortFunction(controller)
    //console.log('Response fully received');
   
  }

  // END ****************** MOVED FROM AsyncLichessStream

function receivedEventString(string) {
  //console.log("***** GOT GAME STRING ->",string)

  // The event stream has to finish before we leave the screen
  if (string == "ABORT") {
    hasActiveLichessStream = false
    // timer will send them to calling View
    return
  }

  hasActiveLichessStream = true

  // getting all the stream traffic so we know it is alive
  if (string == "\n")
    return

  const entries = string.split("\n");
  //console.log("***** Game Entries",entries)
  if (entries.length == 0)
    return;
  for (var i=0; i < entries.length; i++) {
    if (entries[i].length == 0)
      continue
    const oneEntry = JSON.parse(entries[i])
    //console.log("*** entry ",i,oneEntry)
    if (oneEntry.hasOwnProperty('type')) {
      switch (oneEntry['type']) {
        case "gameFull" :
          processingGameFull = true
          parseGameFull(oneEntry)
          break
        case "gameState" :
          processingGameFull = false
          parseGameState(oneEntry)
          break
        case "chatLine" :
          break
        case "opponentGone" :
          break
      }
    }

  }
}

function parseGameFull(json) {
  const whiteJson = json['white']
  const blackJson = json['black']
  const stateJson = json['state']

  const whiteId = whiteJson.hasOwnProperty('id') ? whiteJson['id'] : "ai";
  const whiteName = whiteJson.hasOwnProperty('name') ? whiteJson['name'] : "AI";
  const blackId = blackJson.hasOwnProperty('id') ? blackJson['id'] : "ai";
  const blackName = blackJson.hasOwnProperty('name') ? blackJson['name'] : "AI";

  if (whiteId == lichessUserid) {
    playingColorString = "w"
    opponentUsername = blackName
  }
  else {
    playingColorString = "b"
    opponentUsername = whiteName
  }

  // display the opponent name
  opponentNameDisplay.value = opponentUsername
  
  // initial dispay on the board
  orientation.value = (playingColorString == "w") ? "white" : "black"

  parseGameState(stateJson,true)

}

function showConfetti() {
  var element = document.getElementById("app")

  party.resolvableShapes["whiteKing"] = "<img src='img/chesspieces/wikipedia/wK.svg'/>"
  party.resolvableShapes["whiteQueen"] = "<img src='img/chesspieces/wikipedia/wQ.svg'/>"
  party.resolvableShapes["blackKing"] = "<img src='img/chesspieces/wikipedia/bK.svg'/>"
  party.resolvableShapes["blackQueen"] = "<img src='img/chesspieces/wikipedia/bQ.svg'/>"

  party.sparkles(element, {
    count: party.variation.range(100, 200),
    speed: party.variation.range(5, 20),
    shapes: ["whiteKing","whiteQueen","blackKing","blackQueen"]
  });
}

function parseGameOver(status,winner) {
  availableMoves.value = []
  gameTimerIsCountingDown = false

  const displayWinner = (winner != "") ? "  " + t("Winner") + ":" + winner : ""
  statusDisplay.value = t("Game Over") + ": " + status + displayWinner

  if (orientation.value == winner && isSubscriber.value)
    showConfetti()
}

function parseGameState(json) {

  //console.log("**** PARSE GAME STATE ",json)
  const status = json.hasOwnProperty('status') ? json['status'] : ""
  const winner = json.hasOwnProperty('winner') ? json['winner'] : ""

  if (status == "created")
    return
  if (status != "started") {
    parseGameOver(status,winner)
    return
  }
  const moves = json['moves']
  const wtime = json['wtime']
  const btime = json['btime']

  const whiteDraw = json.hasOwnProperty('wdraw') ? json['wdraw'] : ""
  const blackDraw = json.hasOwnProperty('bdraw') ? json['wdraw'] : ""

  // https://github.com/lichess-org/scalachess/blob/master/src/main/scala/Status.scala


  const plyCount = (moves.length == 0) ? 0 : moves.split(" ").length
  const colorToMove = (plyCount % 2) ? "b" : "w"

  playerSecondsLeft = (playingColorString == "w") ? wtime : btime
  opponentSecondsLeft = (playingColorString == "w") ? btime : wtime

  if (wtime == lichessNoTimerValue) {
    if (processingGameFull) {
      opponentTimeDisplay.value = t("Not Timed")
      playerTimeDisplay.value = t("Not Timed")
    }
  }
  else {

    // if from "gameFull", set timer if needed
    if (processingGameFull) {
      gameTimerIsCountingDown = false
      startTimer()
    }

    updateTimerViews()

    if (colorToMove == playingColorString) {
        gameTimerAgainstPlayer = true
        timerAmount = Math.floor(playerSecondsLeft / 1000)
    }
    else {
        gameTimerAgainstPlayer = false
        timerAmount = Math.floor(opponentSecondsLeft / 1000)
    }
    gameTimerIsCountingDown = true
  }

  if (colorToMove == playingColorString) {
    statusDisplay.value = t("Make Move")
    processLichessMoveSequence(moves,true)
  }
  else {
    if (processingGameFull)
      processLichessMoveSequence(moves,false)
    statusDisplay.value = t("Waiting") + "..."
  }
}

  // START ALERT REPLACEMENT

  const openAlertReplacementModal = async (header,message,type,task) => {
    const modal = await modalController.create({
      component: ModalAlertReplacement,
      componentProps: {
        header : header,
        message : message,
        alertType : type,
        task : task    
       }
    });

    modal.present();

    const { data, task1 } = await modal.onWillDismiss();

    if (data == "no")
      return
    alertResult(data,task)
  };

  // END ALERT REPLACEMENT

  function setupAlert(header,message,type,task) {
    if (showBigButtons.value)
      openAlertReplacementModal(header,message,type,task)
    else {
      alertInfo.value = { header : header, message : message, type : type, task : task  }
      alertTrigger.value++
    }
  }

  function alertResult(response,task,info) {
    //console.log("Alert Result",response,task)

    if (response == "no")
      return

    setResign()
  }

function toolbarClick(arrayIndex) {
  //console.log("toolbar click and game state ",arrayIndex,gameState)
  switch (arrayIndex) {
    case TOOLBAR_UNDO :
      if (canUndoBigButtonSelection) {
        getAvailableMoves(displayFen)
        canUndoBigButtonSelection = false
        toolbarElements.value[TOOLBAR_UNDO].disabled = true
        toolbarTrigger.value++
      }
      break
    case TOOLBAR_OPEN_GAME_LIST :
      emit("next-item-click","lichess_open_gamelist","Open Game List",whichMenu)
      break
    case TOOLBAR_RESIGN :
      if (gameState != GAME_ENDED)
        setupAlert(t("Resign Game?"),t("Do you want to resign the game?"),"yesno","resign")

      break
  }
}

function processLichessPlayerResignJson(json) {
  //console.log("**** RESPONSE BACK FROM LICHESS AFTER RESIGN",json)
  gameState = GAME_ENDED
}

function setResign() {
  // player is resigning or aborting game, send that to Lichess.org
  const formData = {
  }
  const action = canAbortGame() ? "abort" : "resign"
  const urlStr = "https://lichess.org/api/board/game/" + openGameId + "/" + action
  getLichessData(urlStr, lichessAccessToken,formData, processLichessPlayerResignJson) 
}

function eyegazeClick(eyegazeInfo) {
  // console.log("BUTTON CLICKED", eyegazeInfo.moves,availableMoves.value.length)
  // console.log("button move",eyegazeInfo.moves[0])
  // console.log("want fen",eyegazeInfo.moves[0].after)
  // console.log("available moves",availableMoves.value)
  if (eyegazeInfo.moves.length == 1) {
    for (var i=0; i < availableMoves.value.length; i++)
      if (eyegazeInfo.moves[0].after == availableMoves.value[i].fen) {
        //console.log("*** BUTTON MOVE MATCH",i)

        canUndoBigButtonSelection = false
        toolbarElements.value[TOOLBAR_UNDO].disabled = true
        toolbarTrigger.value++

        boardMove(i)
        break
      }
  }
  else {

    canUndoBigButtonSelection = true
    toolbarElements.value[TOOLBAR_UNDO].disabled = false
    toolbarTrigger.value++

    eyegazeMatrix.value = buildEyegazeButtons(eyegazeInfo.moves,isPortrait,blindnessLevel)
    // toolbarElements.value[TOOLBAR_TAKEBACK_OPTIONS].disabled = false
    // toolbarElements.value[toolbarArrayOffset(TOOLBAR_TAKEBACK_OPTIONS)].label = t("Undo")
    // toolbarTrigger.value++
  }

}

function boardMove(arrayIndex,from,to) {
  //console.log("board move ",arguments)
  const oneMove = availableMoves.value[arrayIndex]
  //console.log("**** onemove in board move",arrayIndex,oneMove)
  forceFen.value = oneMove.fen

  availableMoves.value = []
  eyegazeMatrix.value = [[]]
  processValidMove(oneMove)
}

function incrementAppendTrigger() {
  if (blankAppendTrigger.length > 64)
    blankAppendTrigger = ""
  blankAppendTrigger += " "
}

function formatTime(timeInSeconds) {
  var hoursString = ""
  var minutesString = ""
  var secondsString = ""

  const hours = Math.floor(timeInSeconds / 3600)
  const minutesSeconds = Math.floor(timeInSeconds % 3600)
  const minutes = Math.floor(minutesSeconds / 60)
  const seconds = Math.floor(minutesSeconds % 60)

  if (seconds >= 10)
      secondsString = ":" + seconds
  else
      secondsString = ":0" + seconds
  if (hours > 0) {
    hoursString = hours + ":"
    if (minutes >= 10)
      minutesString = minutes
    else
      minutesString = "0" + minutes
  }
  else {
    hoursString = "";
    if (minutes > 0)
      minutesString = minutes
    else
      minutesString = "";
  }
  return hoursString + minutesString + secondsString
}

function updateTimerViews() {
  const playerTimeString = formatTime(Math.floor(playerSecondsLeft / 1000))
  let opponentTimeString = formatTime(Math.floor(opponentSecondsLeft / 1000))
  opponentTimeDisplay.value = opponentTimeString
  playerTimeDisplay.value = playerTimeString
}

function startTimer() {
  
  function oneSecondInterval() {
    if (timerCanStop || timerAmount < 0) {
      clearInterval(id)
      return
    }

    if (gameTimerIsCountingDown) {
      timerAmount--
      const timeString = formatTime(timerAmount)
      if (gameTimerAgainstPlayer)
        playerTimeDisplay.value = timeString
      else
        opponentTimeDisplay.value = timeString
    }
  }
  var id = setInterval(oneSecondInterval, 1000)
}

function getAvailableMoves(fen) {
  var fenPieces = fen.split(" ")
  fenPieces[5] = 1
  const fenFixed = fenPieces.join(" ")
  chess.load(fenFixed)
  const moves = chess.moves({ verbose: true })
  availableMoves.value = buildAvailableMoves(moves,false)

  if (showEyegazeButtons.value)
    eyegazeMatrix.value = buildEyegazeButtons(moves,isPortrait,blindnessLevel)
}

onBeforeUnmount(() => {
  //console.log("**************** Lichess Game unmounted")
  stopWaiting = true
  timerCanStop = true
  
  isDoneWithLichessStream = true

  // used for the client doing the stream
  //if (streamAbortCallback != null)
  //  streamAbortCallback(streamController)

})

function canAbortGame() {
  return moveNumber <= 1
}

function updateForceFen() {

    let timerSleepAmount = 250

    function oneInterval() {
      clearInterval(id)

      forceFen.value = displayFen
    }
    var id = setInterval(oneInterval, timerSleepAmount)
  }

function processLichessMoveSequence(moveString,isPlayerMove) {

  // the "moveString" is a space separated list of UCI moves getting to the current board position

  //console.log("processLichessMoveSequence",moveString)
  chess.load(INITIAL_FEN)
  if (moveString.length > 0) {
    const moves = moveString.split(" ")
    for (var i=0; i < moves.length; i++) {
      chess.move(moves[i])
    }
  }
  const sanHistoryArray = chess.history()
  var text = ""
  var firstply = 0
  moveNumber = 0

  const plysToDisplay = (showEyegazeButtons.value) ? 10 : 24
  if (sanHistoryArray.length > plysToDisplay) {
    firstply = Math.floor((sanHistoryArray.length-plysToDisplay)/ 2) * 2
    moveNumber = firstply / 2
  }
  for (var i=firstply; i < sanHistoryArray.length; i++) {
    if (i % 2 == 0) {
      moveNumber++
      text += moveNumber + "."
    }
    text += sanHistoryArray[i] + " "
  }
  textareaMessage.value = text

  displayFen = chess.fen()
  animateFen.value = [forceFen.value , displayFen]
  updateForceFen()
  //console.log("display FEN",displayFen)

  if (isPlayerMove)
    getAvailableMoves(displayFen)
}

function processLichessPlayerMoveJson(json) {
  //console.log("**** RESPONSE BACK FROM LICHESS AFTER MOVE",json)

  // WORK - check for error
  if (json.hasOwnProperty('error')) {
    //console.log("here")
    const explainTitle = "*** ERROR ALERT ***"
    const explainMessage = "ERROR while sending move to Lichess -> " + urlSentToLichess + "\n" +
                "Length " + urlSentToLichess.length + "\n" +
                "*** You need to back out to the Lichess Menu and bring up 'Open Games List'\n\n" +
                "*** PLEASE send this message to henry@chessvis.com"
    alert(explainMessage)
    return;
  }

  // let the stream processing handle time or game status updates

}

// player made a valid move from the list of valid moves. Send it to Lichess.org
function processValidMove(oneMove) {

  const fileLetters = [ "a", "b", "c", "d", "e", "f", "g", "h"]
  const fenString = oneMove.fen
  const from = oneMove.from
  const to = oneMove.to
  const promotion = oneMove.promotion
  const san = oneMove.san
  
  const moveDots = (playingColorString == "w") ? ". " : "... "
  const moveNumberForMove = (playingColorString == "w") ? moveNumber + 1 : moveNumber
  textareaMessage.value = moveNumberForMove + moveDots + san
  availableMoves.value = []    
  
  const fromRank = 8 - Math.floor(from/8)
  const fromFile = fileLetters[from % 8]
  const toRank = 8 - Math.floor(to/8)
  const toFile = fileLetters[to % 8]
  
  var uci = fromFile + fromRank + toFile + toRank
  if (promotion != "" && promotion != " ")
    uci += promotion

  const formData = {
  }
  urlSentToLichess = "https://lichess.org/api/board/game/" + openGameId + "/move/" + uci
  //console.log("send move to lichess    ",urlSentToLichess)
  //console.log("   uci sent & length -> ",uci,uci.length)
  getLichessData(urlSentToLichess, lichessAccessToken,formData, processLichessPlayerMoveJson) 
}

</script>

<style scoped>

#play-container {
position : relative;
}

.name-text {
display: inline-block;
width : 48%;
margin-left : 2%;
position : relative;
text-align : left;
}

.time-text {
display: inline-block;
width : 48%;
margin-right : 2%;
position : relative;
text-align : right;
}


</style>
